// =====================================================================
// src/lib/v2/flags.js
//
// Feature flag layer for V2 Business Connect.
//
// How it works:
//   1. TEST_UIDS list (below) — these specific user accounts ALWAYS get V2
//   2. Firebase Remote Config `v2_business_connect` — global toggle (false now)
//
// During Phase 1-4: only TEST_UIDS see V2. Everyone else stays on V1.
// At Phase 5 launch: flip the Remote Config flag to `true` for global rollout.
//
// =====================================================================

import { useState, useEffect } from 'react';
import { getRemoteConfig, fetchAndActivate, getValue } from 'firebase/remote-config';
import { app } from '../firebase';

// ============================================================
// TEST USER UIDS — version-controlled allowlist
// ============================================================
//
// These 3 accounts ALWAYS see V2, regardless of Remote Config.
// Real V1 users (everyone else) NEVER see V2 until Phase 5 launch.
//
// To add/remove a test user later, edit this list and commit to git.
// No Firebase config change needed.
//
const TEST_UIDS = [
  'YYCzVhMQeRTfRatuovwelQ4oM8d2',  // rajasekhar91.d@gmail.com
  'RS0iaHZppgQeaNT4bGt7KWRbWyZ2',  // aangansupport@gmail.com
  '2ZYHrGVLcDgibj4YXxT7hkEBqeF3',  // test1@aangan.com
];

// ============================================================
// USER ID NORMALIZATION
// ============================================================
//
// Different parts of the app pass different user-object shapes:
//   - V1's App.jsx maps Firebase Auth → its own shape with .id
//   - Some V2 entry points use the raw Firebase Auth user with .uid
//
// Read both so neither caller has to remember which shape they passed.
//
function getUid(user) {
  return user?.id || user?.uid || null;
}

// ============================================================
// REMOTE CONFIG INITIALIZATION
// ============================================================

let rcInstance = null;
let rcReady = false;
let rcReadyPromise = null;

function initRemoteConfig() {
  if (rcInstance) return rcInstance;
  rcInstance = getRemoteConfig(app);

  // Cache: 1 minute during dev, 1 hour in production.
  // Short cache during dev so we can change the flag in Firebase Console
  // and see updates without waiting an hour.
  rcInstance.settings.minimumFetchIntervalMillis = import.meta.env.DEV
    ? 60 * 1000          // 1 min during local dev
    : 60 * 60 * 1000;    // 1 hour in production

  // Defaults applied when fetch fails or before first fetch.
  // SAFE STATE: v2 stays OFF until proven otherwise.
  rcInstance.defaultConfig = {
    v2_business_connect: false,
  };

  return rcInstance;
}

async function ensureRcReady() {
  if (rcReady) return;
  if (rcReadyPromise) return rcReadyPromise;

  rcReadyPromise = (async () => {
    try {
      const rc = initRemoteConfig();
      await fetchAndActivate(rc);
      rcReady = true;
    } catch (err) {
      // Network failure / project misconfig — fall back to defaults.
      // Don't throw — V2 just stays off, which is the safe state.
      console.warn('[v2/flags] Remote Config fetch failed, using defaults:', err?.message);
      rcReady = true;
    }
  })();

  return rcReadyPromise;
}

// ============================================================
// PUBLIC API
// ============================================================

/**
 * Returns the V2 Business Connect flag for the given user.
 *
 * Two layers (any one true → V2 enabled):
 *   1. User UID is in TEST_UIDS list (instant — no network)
 *   2. Remote Config `v2_business_connect` is true (network fetch)
 *
 * Accepts either { id } (V1 user shape) or { uid } (Firebase Auth raw shape).
 *
 * @param {object|null} currentUser - The authenticated user
 * @returns {Promise<boolean>}
 */
export async function isV2Enabled(currentUser) {
  // Layer 1: test UID allowlist — fast, no network
  const uid = getUid(currentUser);
  if (uid && TEST_UIDS.includes(uid)) {
    return true;
  }

  // Layer 2: Remote Config
  await ensureRcReady();
  try {
    const value = getValue(initRemoteConfig(), 'v2_business_connect');
    return value.asBoolean();
  } catch (err) {
    console.warn('[v2/flags] getValue failed:', err?.message);
    return false; // safe default
  }
}

/**
 * Synchronous check — only checks the test UID list.
 * Use this when you can't await (e.g., early render gates).
 *
 * Note: ignores Remote Config. Returns false for production users
 * even when the flag is flipped to true. Only useful for test-user
 * detection.
 */
export function isV2EnabledSync(currentUser) {
  const uid = getUid(currentUser);
  return !!(uid && TEST_UIDS.includes(uid));
}

/**
 * React hook — returns current v2 flag value and updates reactively
 * when the user changes or Remote Config resolves.
 *
 * Usage in App.jsx:
 *   import { useV2Flag } from './lib/v2/flags';
 *   const v2Enabled = useV2Flag(currentUser);
 */
export function useV2Flag(currentUser) {
  const [enabled, setEnabled] = useState(() => isV2EnabledSync(currentUser));
  const uid = getUid(currentUser);

  useEffect(() => {
    let cancelled = false;
    isV2Enabled(currentUser).then(result => {
      if (!cancelled) setEnabled(result);
    });
    return () => { cancelled = true; };
  }, [uid]);

  return enabled;
}
